import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { Search, X } from "lucide-react";
import { fetcher } from "../../lib/api";
import { ProductGrid } from "../../components/public/ProductGrid";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";

const GENDER_CHIPS = [["", "Todos"], ["masculino", "Masculinos"], ["feminino", "Femininos"], ["unissex", "Unissex"]];
const TAG_CHIPS = [["best", "Mais vendidos"], ["launch", "Lançamentos"], ["promo", "Promoções"]];
const TITLES = { best: "Mais vendidos", launch: "Lançamentos", promo: "Promoções", featured: "Destaques" };

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`whitespace-nowrap rounded-full border px-5 py-2 text-xs uppercase tracking-[0.15em] transition-colors ${active ? "border-[#D4AF37] bg-[#D4AF37] text-[#0B0B0C]" : "border-[#D4AF37]/25 text-[#A1A1AA] hover:border-[#D4AF37]/60 hover:text-[#F3E5AB]"}`}>
    {children}
  </button>
);

function FilterSelect({ value, onChange, placeholder, options, testid }) {
  return (
    <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger className="h-11 w-full rounded-full border-[#D4AF37]/25 bg-transparent px-5 text-sm sm:w-48" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o[0]} value={o[0]}>{o[1]}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const get = (k) => params.get(k) || "";
  const [q, setQ] = useState(get("q"));
  const { data: filters } = useSWR("/public/filters", fetcher);
  const { data: items } = useSWR(`/public/products?${params.toString()}`, fetcher, { refreshInterval: 60000, keepPreviousData: true });

  useEffect(() => {
    setQ(params.get("q") || "");
    document.title = "Catálogo | EMAIA PERFUMES";
  }, [params]);

  useEffect(() => {
    if (q.trim() === (params.get("q") || "")) return undefined;
    const t = setTimeout(() => set("q", q.trim()), 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const set = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };
  const toggleTag = (t) => set("tag", get("tag") === t ? "" : t);

  return (
    <div className="mx-auto max-w-7xl px-5 pb-10 pt-14 lg:px-8" data-testid="catalog-page">
      <p className="eyebrow mb-3">Catálogo</p>
      <h1 className="font-serif text-4xl font-medium text-[#FAF6F0] sm:text-5xl lg:text-6xl">{TITLES[get("tag")] || "Todas as fragrâncias"}</h1>

      <div className="mt-10 space-y-5 rounded-2xl border border-[#D4AF37]/12 bg-[#141417]/60 p-5 sm:p-6">
        <div className="no-scrollbar flex gap-2 overflow-x-auto">
          {GENDER_CHIPS.map(([v, l]) => <Chip key={l} active={get("gender") === v} onClick={() => set("gender", v)} testid={`filter-gender-${v || "todos"}`}>{l}</Chip>)}
          <span className="mx-2 w-px shrink-0 bg-white/10" />
          {TAG_CHIPS.map(([v, l]) => <Chip key={v} active={get("tag") === v} onClick={() => toggleTag(v)} testid={`filter-tag-${v}`}>{l}</Chip>)}
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <form onSubmit={(e) => { e.preventDefault(); set("q", q.trim()); }} className="relative flex-1 sm:min-w-[240px]">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#D4AF37]/70" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome do perfume ou marca" className="h-11 w-full rounded-full border border-[#D4AF37]/25 bg-transparent pl-11 pr-4 text-sm outline-none focus:border-[#D4AF37]/60" data-testid="catalog-search-input" />
          </form>
          <FilterSelect value={get("brand")} onChange={(v) => set("brand", v)} placeholder="Todas as marcas" options={(filters?.brands || []).map((b) => [b, b])} testid="filter-brand-select" />
          <FilterSelect value={get("family")} onChange={(v) => set("family", v)} placeholder="Família olfativa" options={(filters?.families || []).map((f) => [f, f])} testid="filter-family-select" />
          <FilterSelect value={get("sort")} onChange={(v) => set("sort", v)} placeholder="Relevância" options={[["price_asc", "Menor preço"], ["price_desc", "Maior preço"], ["name", "Nome (A-Z)"]]} testid="filter-sort-select" />
          {params.toString() && (
            <button onClick={() => setParams({}, { replace: true })} className="flex items-center gap-1 px-3 text-xs text-[#A1A1AA] hover:text-[#F3E5AB]" data-testid="filter-clear"><X className="h-3.5 w-3.5" /> Limpar</button>
          )}
        </div>
      </div>

      <div className="mt-10">
        {!items && (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="aspect-[4/6] rounded-2xl bg-[#141417]" />)}</div>
        )}
        {items && (
          <>
            <p className="mb-6 text-sm text-[#A1A1AA]" data-testid="catalog-count">{items.length} {items.length === 1 ? "fragrância encontrada" : "fragrâncias encontradas"}</p>
            {items.length ? <ProductGrid items={items} testid="catalog-grid" /> : (
              <div className="py-24 text-center" data-testid="catalog-empty">
                <p className="font-serif text-3xl text-[#FAF6F0]">Nenhum perfume encontrado</p>
                <p className="mt-3 text-sm text-[#A1A1AA]">Ajuste os filtros ou fale conosco pelo WhatsApp.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
